import { ExternalLink, BookUser } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import type { Database } from '@/types/supabase';
import { ServiceResult } from '@/utils/service/serviceUtils';
import { Button } from "@/components/ui/button";

type PoemDetails = Database['public']['Views']['poem_details']['Row'];
type Poem = Database['public']['Tables']['poems']['Row'];

interface GeneratedPoemCardProps {
  poem: PoemDetails | ServiceResult<Poem> | ServiceResult<PoemDetails> | any;
}

const GeneratedPoemCard = ({ poem }: GeneratedPoemCardProps) => {
  // Handle ServiceResult format
  const poemData = poem && 'data' in poem ? poem.data : poem;
  
  // Validate poem data to prevent rendering errors
  if (!poemData || typeof poemData !== 'object') {
    console.error("Invalid poem data received:", poem);
    return (
      <Card className="overflow-hidden book-shadow p-6">
        <CardContent className="p-0">
          <div className="text-center text-destructive">
            <p>Error: Invalid poem data received</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Ensure content is a string to prevent crashes
  const content = typeof poemData.content === 'string' ? poemData.content : '';
  const title = typeof poemData.title === 'string' ? poemData.title : 'Untitled Poem';
  
  // Better poet name handling with multiple fallbacks
  let poetName = 'Unknown Poet';
  // First try from poem_details view
  if (typeof poemData.poet_name === 'string' && poemData.poet_name) {
    poetName = poemData.poet_name;
  } 
  // Then try from the original payload
  else if (poem && typeof poem.poet_name === 'string' && poem.poet_name) {
    poetName = poem.poet_name;
  }
  // Then try from the selectedPoet if available
  else if (poemData.selectedPoet && typeof poemData.selectedPoet.name === 'string') {
    poetName = poemData.selectedPoet.name;
  }
  // If we have a poet_id but no name, try to get name from metadata 
  else if (poemData.metadata && poemData.metadata.poetName) {
    poetName = poemData.metadata.poetName;
  }
  
  // Handle subject field if present
  const subject = typeof poemData.subject === 'string' && poemData.subject 
    ? poemData.subject 
    : 'this topic';
  
  const isLongPoem = content.split('\n').length > 10;

  return (
    <div className="w-full max-w-4xl mx-auto animate-fade-in" data-testid="generated-poem">
      <Card className="overflow-hidden book-shadow">
        <CardContent className="p-6">
          <div className="max-w-2xl mx-auto w-full">
            <div className="mb-4 p-3 bg-primary/10 rounded-md flex justify-between items-center">
              <p className="text-sm font-medium">This poem has been added to the big book of poems about {subject}.</p>
              <Link
                to={`/book?poet=${encodeURIComponent(poetName)}&poemId=${poemData.id}&subject=${encodeURIComponent(subject)}`}
                className="text-primary font-medium text-sm hover:underline flex items-center"
              >
                View in Book <ExternalLink className="ml-1 h-3 w-3" />
              </Link>
            </div>
            
            <h2 className="font-poetry text-3xl font-semibold mb-3 text-[#403E43] text-center" data-testid="poem-title">
              {title}
            </h2>
            
            <div className="flex justify-center items-center mb-6">
              <div className="text-sm text-muted-foreground italic text-center">
                <em>Inspired by the style of</em>{" "}
                <span className="font-semibold text-[#403E43]" data-testid="poet-name">{poetName}</span>
              </div>
            </div>
            
            {isLongPoem ? (
              <ScrollArea className="h-[calc(100vh-400px)] min-h-[250px] rounded-lg border border-[#0000001a] bg-[#F1F0FB]/30">
                <div className="text-sm leading-relaxed whitespace-pre-wrap font-mono text-[#221F26] p-6" data-testid="poem-content">
                  {content}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-sm leading-relaxed whitespace-pre-wrap font-mono text-[#221F26] p-6 rounded-lg border border-[#0000001a] bg-[#F1F0FB]/30" data-testid="poem-content">
                {content}
              </div>
            )}
            
            {/* Poet information section */}
            {poetName !== 'Unknown Poet' && (
              <div className="mt-6 pt-4" data-testid="poet-info-section">
                <Separator className="mb-4" />
                <div className="flex items-start space-x-2">
                  <BookUser className="h-5 w-5 text-[#8B5CF6] mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium text-[#403E43] mb-2">About {poetName}'s Style</h3>
                    
                    {/* Style Points Section */}
                    <div>
                      {poemData.style_points ? (
                        Array.isArray(poemData.style_points) ? (
                          // Handle array of strings format
                          <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground ml-1 mb-3">
                            {(poemData.style_points as string[]).map((point, index) => (
                              <li key={index}>{point}</li>
                            ))}
                          </ul>
                        ) : typeof poemData.style_points === 'object' ? (
                          // Handle object format (key-value pairs)
                          <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground ml-1 mb-3">
                            {Object.entries(poemData.style_points as Record<string, any>).map(([key, value], index) => (
                              <li key={index}>
                                <strong>{key}:</strong> {typeof value === 'number' ? value.toFixed(1) : String(value || '')}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          // Fallback for string style_description
                          <p className="text-sm text-muted-foreground mb-3" data-testid="poet-style-points">
                            {poemData.style_description || "No style information available."}
                          </p>
                        )
                      ) : poemData.style_description ? (
                        // If we have style_description but not style_points
                        typeof poemData.style_description === 'string' ? (
                          // If style_description is a string with bullet points, convert to list
                          poemData.style_description.includes('- ') ? (
                            <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground ml-1 mb-3">
                              {poemData.style_description.split('- ').filter(Boolean).map((point: string, index: number) => (
                                <li key={index}>{point.trim()}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-muted-foreground mb-3" data-testid="poet-style-points">
                              {poemData.style_description}
                            </p>
                          )
                        ) : (
                          <p className="text-sm text-muted-foreground mb-3">No style information available.</p>
                        )
                      ) : (
                        <p className="text-sm text-muted-foreground mb-3">No style information available.</p>
                      )}
                    </div>
                    
                    {/* Poetry Foundation Link */}
                    <div className="mt-4">
                      {poemData.poetry_foundation_url && (
                        <Button variant="outline" className="w-full" asChild>
                          <a
                            href={poemData.poetry_foundation_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center"
                            data-testid="poet-link"
                          >
                            <BookUser className="mr-2 h-4 w-4" />
                            Learn More
                            <ExternalLink className="ml-2 h-3 w-3" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GeneratedPoemCard; 